import { token, type TokenName } from '@bp-files/ui';

export interface BrandMarkProps {
  /** Rendered height. Width follows the mark's 177.74 × 80.04 aspect ratio. */
  height?: number;
  /** Accessible label. Omit when adjacent text already names the product. */
  label?: string;
  /** Fill colour, as a design token. Defaults to the brand blue. */
  color?: TokenName;
}

const ASPECT = 177.74 / 80.04;

/**
 * The Blue Partner "BP" monogram.
 *
 * Paths copied verbatim from `design/design-system/assets/logo-mark-blue.svg`.
 * Inlined rather than loaded as a file so it inherits `currentColor` and can be
 * tinted per context via the `color` prop — the `fill` on the paths is removed
 * for exactly that reason. The rail renders it in `bp-text` on `bp-rail`; other
 * contexts may tint it blue instead.
 *
 * **`bp-text`, not `bp-on-accent`** (ADR-0064). Every chrome caller passed
 * `bp-on-accent` while there was one palette, where the two are #e0e0e0 and
 * #ffffff and indistinguishable at 18px — but `bp-on-accent` means *the label
 * on an accent fill*, and the rail is not one. On the daylight palette that
 * reading is the difference between ink and a white mark on a white rail. The
 * rule for any caller: `bp-on-accent` only where the background is
 * `bp-blue`/`bp-blue-deep`.
 */
export function BrandMark({ height = 24, label, color = 'bp-blue' }: BrandMarkProps) {
  return (
    <svg
      viewBox="0 0 177.74 80.04"
      height={height}
      width={height * ASPECT}
      fill="currentColor"
      style={{ color: token(color) }}
      {...(label !== undefined
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true, focusable: false })}
    >
      <path d="M139.1,59.19c-9.04.07-18.08.02-27.38.02v20.83h-24.68v-1.39c7.55-3.66,11.32-12.08,11.32-18.62,0-13.77-8.8-20.01-8.94-20.01-.04,0,1.39-.41,2.75-1.26,8.69-.01,34.68-.05,47.26-.07,2.55,0,13.47,0,13.49-9.26.01-6.57-6.52-9.16-13.49-9.18-28.12-.07-87.01,0-88.36,0C55.45,14.09,65.24.02,65.24.02,65.24.02,117.51,0,141.82,0,150.7,0,177.74,3.34,177.74,29.42c0,28.5-29.9,29.7-38.64,29.77Z" />
      <path d="M82.46,40c-.13,0,11.04-4.58,11.04-15.71h-24.12c.94,3.96-1.44,6.75-7.83,6.99-2.16.08-35.89,0-36.89,0v-11.02c6.7,0,21.53-.03,21.53-.03,0,0,13.01-18.67,14.19-20.23C40.04,0,20.11.02,0,.02v80.02h61.55c21.3,0,31.95-9.02,31.95-20.02,0-13.77-10.89-20.02-11.04-20.02ZM61.55,59.77c-11.98.02-36.14.02-36.89,0v-11.02s25.21.02,36.89,0c4.13,0,7.99,1.55,7.99,5.53s-3.86,5.51-7.99,5.49Z" />
    </svg>
  );
}
